import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ExtensionApiFactory, NetworkDetails } from '~cdw-ae/common/frontend/browser-extension-api';

import { type RootState } from '../extension-store/extension.store';
import { BaseInitialState, StateStatus } from '../extension-store/interfaces/extension-store.interfaces';

export interface NetworkStatus {
  isOnline: boolean;
  publicIp: string;
  privateIp: string;
}

interface InitialNetworkStatusState extends BaseInitialState {
  networkStatus: NetworkStatus;
  privateIpStatus: StateStatus;
  publicIpStatus: StateStatus;
}

const initialState: InitialNetworkStatusState = {
  error: null,
  status: StateStatus.IDLE,
  privateIpStatus: StateStatus.IDLE,
  publicIpStatus: StateStatus.IDLE,
  networkStatus: {
    isOnline: false,
    publicIp: '',
    privateIp: '',
  },
};

export const NetworkEffects = {
  getPrivateIp: createAsyncThunk('NetworkStatus/getPrivateIp', async (): Promise<string> => {
    const extensionApi = ExtensionApiFactory();
    const config: NetworkDetails = await extensionApi.getNetworkDetails();
    return config.ipv4 || config.ipv6 || 'unknown';
  }),
};

export const NetworkStatusState = createSlice({
  name: 'NetworkStatusReducer',
  initialState,
  reducers: {
    setOnline: (mutableState) => {
      mutableState.networkStatus.isOnline = true;
    },
    setOffline: (mutableState) => {
      mutableState.networkStatus.isOnline = false;
    },
    setPublicIp: (mutableState, action: PayloadAction<string>) => {
      mutableState.networkStatus.publicIp = action.payload;
      mutableState.publicIpStatus = StateStatus.SUCCESS;
      if (mutableState.privateIpStatus === StateStatus.SUCCESS) {
        mutableState.status = StateStatus.SUCCESS;
      }
    },
    setPrivateIp: (mutableState, action: PayloadAction<string>) => {
      mutableState.networkStatus.privateIp = action.payload;
      mutableState.privateIpStatus = StateStatus.SUCCESS;
      if (mutableState.publicIpStatus === StateStatus.SUCCESS) {
        mutableState.status = StateStatus.SUCCESS;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(NetworkEffects.getPrivateIp.pending, (mutableState) => {
      mutableState.status = StateStatus.LOADING;
      mutableState.privateIpStatus = StateStatus.LOADING;
    });

    builder.addCase(NetworkEffects.getPrivateIp.fulfilled, (mutableState, action) => {
      mutableState.networkStatus.privateIp = action.payload;
      mutableState.privateIpStatus = StateStatus.SUCCESS;
      if (mutableState.publicIpStatus === StateStatus.SUCCESS) {
        mutableState.status = StateStatus.SUCCESS;
      }
    });

    builder.addCase(NetworkEffects.getPrivateIp.rejected, (mutableState, action) => {
      mutableState.privateIpStatus = StateStatus.FAILED;
      mutableState.error = action.error;
    });
  },
});

export const { setOnline, setOffline, setPublicIp, setPrivateIp } = NetworkStatusState.actions;
export const getOnlineStatus = (state: RootState) => state.networkStatus.networkStatus.isOnline;
export const getPublicIp = (state: RootState) => state.networkStatus.networkStatus.publicIp;
export const getPrivateIp = (state: RootState) => state.networkStatus.networkStatus.privateIp;
